import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Mail,
  Globe,
} from 'lucide-react';

/**
 * AI Sourcing & Resume Screening — lean product showcase page.
 *
 * Covers the top of the hiring funnel: AI auto-sourcing (proactively
 * finding and inviting best-fit candidates, plus collecting inbound
 * applicants from the career page, 24/7) and AI resume screening (every
 * resume parsed and scored against the JD across ~8 dimensions, then
 * ranked by fit and auto-routed onward). Employer-facing, rendered in the
 * SearchJob.ai landing theme (cream base, forest-green primary, gradient
 * accents, rounded-3xl cards, dark-ink bands). Structure mirrors
 * StaffingServices.jsx: split hero, alternating feature rows, a dark stats
 * band and a dark final CTA. Helpers are defined locally.
 *
 * Route: /hirewithus/ai-sourcing-screening
 */

const INK = 'hsl(164 33% 7%)';

export default function AISourcingScreening() {
  const navigate = useNavigate();

  useSEO({
    title:
      'AI Sourcing & Resume Screening — Source, Score & Rank Candidates | SearchJob.ai',
    description:
      'SearchJob.ai auto-sources candidates and screens every resume with AI — scoring and ranking each one against your job description across multiple dimensions, so your shortlist is ready in minutes, not weeks.',
    canonical: 'https://searchjob.ai/hirewithus/ai-sourcing-screening',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  // Two alternating highlight rows — sourcing, then screening & ranking.
  const featured = [
    {
      eyebrow: 'Auto-sourcing',
      title: 'Best-fit candidates,',
      accent: 'found and invited for you.',
      body: 'The moment a role opens, the platform starts working the top of your funnel — proactively finding and inviting best-fit candidates around the clock, and gathering every applicant who arrives through your career page.',
      points: [
        'Sources proactively from multiple channels',
        'Invites the best matches automatically',
        'Captures inbound career-page applicants',
        'Runs around the clock, no manual effort',
      ],
      img: '/sourcing-auto-cut.png',
      alt: 'AI agent sourcing and inviting candidates across a network',
      flip: false,
    },
    {
      eyebrow: 'AI screening & ranking',
      title: 'Every resume scored,',
      accent: 'ranked by real fit.',
      body: 'Each resume is parsed and scored against your job description across roughly eight dimensions, then candidates are ranked by fit — so the strongest rise to the top and are routed straight to the next assessment.',
      points: [
        'Scored across 8 dimensions vs the JD',
        'Ranked by fit — best candidates first',
        'Consistent and unbiased, every time',
        'Top candidates auto-routed to assessment',
      ],
      img: '/sourcing-rank-cut.png',
      alt: 'AI agent ranking candidates on a leaderboard by match score',
      flip: true,
    },
  ];

  const stats = [
    { v: '8', l: 'Scoring dimensions' },
    { v: '24/7', l: 'Auto-sourcing' },
    { v: 'Minutes', l: 'To a ranked shortlist' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO (split) ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-16">
            <div className="max-w-4xl mx-auto text-center">
              <div>
                <Eyebrow>AI Sourcing &amp; Screening</Eyebrow>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.06] mb-6">
                  Source and screen every candidate,
                  <br />
                  <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    before you lift a finger.
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
                  SearchJob.ai finds best-fit candidates and scores every
                  resume against your job description — then ranks them by
                  fit, so a sharp shortlist is waiting for you in minutes,
                  not weeks.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/signup')}
                    className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
                  >
                    Book a Demo
                  </Button>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <TrustDot text="24/7 auto-sourcing" />
                  <TrustDot text="8-dimension scoring" />
                  <TrustDot text="Ranked by fit" />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ═════════════ FEATURE ROWS ═════════════ */}
        <Section>
          <div className="space-y-16 lg:space-y-24">
            {featured.map((f) => (
              <FeatureRow key={f.eyebrow} {...f} />
            ))}
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark) ═════════════ */}
        <section
          className="relative py-14 lg:py-20 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                By the numbers
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                A ranked shortlist in minutes,{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  not weeks.
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {stats.map((s) => (
                <div key={s.l}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {s.v}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ FINAL CTA (dark) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                    Stop sifting resumes.
                    <br />
                    <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                      Start hiring the best.
                    </span>
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed mb-8">
                    Tell us what you&apos;re hiring for. SearchJob.ai will
                    source the candidates, screen every resume, and hand you
                    a ranked shortlist — so your team spends its time only on
                    the people worth meeting.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      onClick={() => navigate('/hirewithus/contact')}
                      className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                    >
                      Request a Demo
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/hirewithus/demo')}
                      className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </div>

                <div className="lg:pl-8 lg:border-l lg:border-white/10 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                    Talk to our team
                  </p>
                  <DarkContactLine icon={MapPin} text="Bangalore, India" />
                  <DarkContactLine icon={Mail} text="contact@searchjob.ai" />
                  <DarkContactLine icon={Globe} text="www.searchjob.ai" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Local building blocks — mirror the landing / StaffingServices page */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false, id }) {
  return (
    <section
      id={id}
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[480px] mx-auto h-auto drop-shadow-xl"
      />
    </div>
  );
}

function FeatureRow({ eyebrow, title, accent, body, points, img, alt, flip }) {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div className={flip ? 'lg:order-2' : ''}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <SectionHeading plain={title} accent={accent} />
        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
          {body}
        </p>
        <CheckList items={points} />
      </div>
      <ImageCard src={img} alt={alt} className={flip ? 'lg:order-1' : ''} />
    </div>
  );
}

function TrustDot({ text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-4 h-4 text-primary" />
      {text}
    </span>
  );
}

function DarkContactLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-emerald-300" />
      </span>
      <span className="text-sm font-medium text-white/90">{text}</span>
    </div>
  );
}
